<script lang="ts" setup>
import { defineAsyncComponent } from '@@/bits/vue'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSurfaceStore } from '@@/pinia/surface'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { CommentId, Id, Post } from '@@/types'
import SurfacePostCommentsIcon from '@@/vuecomponents/SurfacePostCommentsIcon.vue'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    post: Post & { id: Id }
    usageContext?: 'postView' | 'postExpandedView'
    postReactionIds?: Id[]
    postCommentIds?: CommentId[]
  }>(),
  {
    usageContext: 'postView',
    postReactionIds: () => [],
    postCommentIds: () => [],
  },
)

const SurfacePostReaction = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostReaction.vue'))

const darkModeStore = useDarkModeStore()
const windowSizeStore = useWindowSizeStore()
const expandedPostStore = useExpandedPostStore()
const reactionsStore = useReactionsStore()
const surfaceStore = useSurfaceStore()
const surfaceSettingsStore = useSurfaceSettingsStore()

const isUsedInPostExpandedView = computed(() => props.usageContext === 'postExpandedView')

const colorScheme = computed(() => {
  if (isUsedInPostExpandedView.value) {
    return darkModeStore.isDarkMode ? 'dark' : 'light'
  }
  return surfaceStore.colorScheme
})

/**
 * REACTIONS
 */
const socialContentTopBar = ref<HTMLDivElement>()
const xReactions = computed(() => {
  if (surfaceStore.isScreenshotMode && !surfaceStore.xReactionsInScreenshotMode) {
    return false
  }
  // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
  // the realtime message for new reaction may come before the reaction enable message
  // => wait for reactionData to be available before showing reactions
  const reactionCount = reactionsStore.accumulatedReactionsByWishId[props.post.id]?.totalReactionsCount ?? 0
  return (
    (surfaceSettingsStore.isReactable || props.postReactionIds.length > 0 || reactionCount > 0) &&
    !!reactionsStore.originalReactionData
  )
})

const isBeingReacted = computed(() => reactionsStore.postIdBeingReactedTo === props.post.id)
const editReaction = () => {
  if (windowSizeStore.isPhone) {
    reactionsStore.startEditingReaction({ postId: props.post.id })
    return
  }

  if (!socialContentTopBar.value) return
  if (socialContentTopBar.value.closest('.surface-post')?.classList.contains('noclick')) {
    // Prevent reaction event when the post is being dragged
    return
  }

  const { left, top, width } = socialContentTopBar.value.getBoundingClientRect()

  reactionsStore.startEditingReaction({
    postId: props.post.id,
    dropdownLeft: left,
    dropdownTop: top,
    dropdownWidth: width,
  })
}

/**
 * COMMENTS
 */

const xComments = computed(() => {
  if (surfaceStore.isScreenshotMode && !surfaceStore.xCommentsInScreenshotMode) {
    return false
  }
  return props.post.published === true && (surfaceSettingsStore.isCommentable || props.postCommentIds.length > 0)
})

const showComments = () => {
  if (isUsedInPostExpandedView.value) return
  expandedPostStore.expandPost({
    postCid: props.post.cid,
    xTextPanel: true,
    shouldFocusInput: !windowSizeStore.isPhone,
  })
}
</script>

<template>
  <div
    ref="socialContentTopBar"
    data-testid="socialContentTopBar"
    :class="['flex flex-row justify-between', !isUsedInPostExpandedView && 'flex-1']"
  >
    <SurfacePostReaction
      v-if="xReactions"
      :post="post"
      :is-reacting="isBeingReacted"
      :current-user-id="surfaceStore.user.id"
      :color-scheme="colorScheme"
      :reaction-data="reactionsStore.originalReactionData"
      :usage-context="usageContext"
      :data-pepin-id="isUsedInPostExpandedView && 'expanded-view-react'"
      :data-pepin-trigger="isUsedInPostExpandedView && 'click'"
      @edit="editReaction"
    />
    <div v-else />
    <SurfacePostCommentsIcon
      v-if="xComments"
      :comment-ids="postCommentIds"
      :color-scheme="colorScheme"
      :is-button="!(surfaceStore.xCommentsUnderPosts || isUsedInPostExpandedView)"
      :usage-context="usageContext"
      :data-pepin-id="!isUsedInPostExpandedView && 'post-comment-expand-post'"
      :data-pepin-trigger="!isUsedInPostExpandedView && 'click'"
      @show-comments-panel="showComments"
    />
  </div>
</template>
